import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useIsKenya } from '../hooks/useIsKenya';
import { usePageMeta } from '../hooks/usePageMeta';
import { Section } from '../components/ui/Section';
import { ArrowRight, Loader2, PackageX } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useQuoteModal } from '../hooks/useQuoteModal';
const kenyanProducts = [{
  title: 'Website Design & Development',
  description: 'Custom-built, mobile-first websites designed for Kenyan businesses. Fast-loading, M-Pesa-ready, and optimized for local audiences.',
  price: 'From KES 50,000',
  tag: 'Most Popular',
  color: 'border-[#FF6600]',
  tagColor: 'bg-[#FF6600]'
}, {
  title: 'Brand Identity & Design',
  description: 'Complete brand packages including logo, brand guidelines, stationery, and social media kits tailored for the East African market.',
  price: 'From KES 35,000',
  tag: 'Essential',
  color: 'border-[#00316B]',
  tagColor: 'bg-[#00316B]'
}, {
  title: 'Digital Marketing',
  description: 'SEO, social media management, and paid advertising campaigns designed to reach Kenyan and East African audiences effectively.',
  price: 'From KES 25,000/mo',
  tag: 'Growth',
  color: 'border-emerald-500',
  tagColor: 'bg-emerald-500'
}, {
  title: 'Corporate Media Production',
  description: 'Professional video production, photography, and motion graphics for corporate events, product launches, and brand storytelling.',
  price: 'From KES 40,000',
  tag: 'Premium',
  color: 'border-purple-500',
  tagColor: 'bg-purple-500'
}];
export function ProductRecommendationsPage() {
  const isKenya = useIsKenya();
  const {
    openModal
  } = useQuoteModal();
  const [ip, setIp] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    const controller = new AbortController();
    fetch('https://api.ipify.org?format=json', {
      signal: controller.signal
    }).then(res => res.json()).then(data => {
      if (data.ip) setIp(data.ip);
    }).catch(() => {}).finally(() => setLoading(false));
    return () => controller.abort();
  }, []);
  usePageMeta({
    title: 'Product Recommendations',
    description: 'Personalized digital product recommendations based on your location.'
  });
  return <main className="pt-24" data-id="element-919">
      {/* Hero */}
      <Section background="default" className="!py-10 md:!py-14" data-id="element-920">
        <div className="max-w-3xl" data-id="element-921">
          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="text-3xl md:text-4xl lg:text-5xl font-bold font-heading text-[#0C0C0C] mb-4 leading-tight" data-id="element-922">
            Recommended for You
          </motion.h1>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1
        }} className="text-lg text-gray-600 leading-relaxed" data-id="element-923">
            {loading ? 'Detecting your location…' : isKenya ? 'Digital solutions tailored for the Kenyan market.' : 'We personalize recommendations based on your region.'}
          </motion.p>
        </div>
      </Section>

      {/* Content */}
      <Section background="white" data-id="element-924">
        {loading ? <div className="flex items-center justify-center py-20 text-gray-400" data-id="element-925">
            <Loader2 className="w-6 h-6 animate-spin mr-3" data-id="element-926" />
            Loading recommendations…
          </div> : isKenya /* Kenya — show 4 product cards */ ? <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-927">
            {kenyanProducts.map((product, index) => <motion.div key={product.title} initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: index * 0.1
        }} className={`relative bg-white rounded-2xl border-2 ${product.color} p-6 md:p-8 flex flex-col`} data-id="element-928">
                <span className={`absolute top-4 right-4 ${product.tagColor} text-white text-xs font-medium px-3 py-1 rounded-full`} data-id="element-929">
                  {product.tag}
                </span>

                <h3 className="text-xl font-bold font-heading text-[#0C0C0C] mb-3 pr-20" data-id="element-930">
                  {product.title}
                </h3>
                <p className="text-gray-600 leading-relaxed text-sm mb-6 flex-1" data-id="element-931">
                  {product.description}
                </p>

                <div className="flex items-center justify-between mt-auto" data-id="element-932">
                  <span className="text-lg font-bold text-[#0C0C0C]" data-id="element-933">
                    {product.price}
                  </span>
                  <button onClick={openModal} className="inline-flex items-center gap-2 text-[#FF6600] font-medium text-sm hover:gap-3 transition-all" data-id="element-934">
                    Get Started
                    <ArrowRight className="w-4 h-4" data-id="element-935" />
                  </button>
                </div>
              </motion.div>)}
          </div> /* Not Kenya — empty state */ : <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="flex flex-col items-center justify-center py-20 text-center" data-id="element-936">
            <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-6" data-id="element-937">
              <PackageX className="w-8 h-8 text-gray-400" data-id="element-938" />
            </div>
            <h2 className="text-2xl font-bold font-heading text-[#0C0C0C] mb-3" data-id="element-939">
              No products found
            </h2>
            <p className="text-gray-500 max-w-md mb-8" data-id="element-940">
              We don't have product recommendations for your region yet. Our
              current offerings are tailored for the Kenyan market.
            </p>
            <Link to="/contact" className="inline-flex items-center gap-2 px-6 py-3 bg-[#00316B] text-white rounded-lg text-sm font-medium hover:bg-[#002554] transition-colors" data-id="element-941">
              Contact Us
              <ArrowRight className="w-4 h-4" data-id="element-942" />
            </Link>
          </motion.div>}
      </Section>

      {/* IP Footer */}
      <Section background="default" className="!py-6 md:!py-8" data-id="element-943">
        <div className="text-center" data-id="element-944">
          <p className="text-xs text-gray-400 font-mono" data-id="element-945">{ip || '—'}</p>
        </div>
      </Section>
    </main>;
}